import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { Plus, Trash2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AttributesTab = ({ attributes, setAttributes, token }) => {
  const [editingAttributeType, setEditingAttributeType] = useState('categories');
  const [newAttributeItem, setNewAttributeItem] = useState({ name: '' });
  
  const headers = { Authorization: `Bearer ${token}` };

  const addItem = async () => {
    if (!newAttributeItem.name.trim()) return;
    try {
      await axios.post(`${API}/settings/attributes/${editingAttributeType}/item`, 
        { name: newAttributeItem.name.trim() }, 
        { headers }
      );
      const res = await axios.get(`${API}/settings/attributes/all`, { headers });
      setAttributes(res.data);
      setNewAttributeItem({ name: '' });
    } catch (error) {
      alert('Error adding item');
    }
  };

  const toggleItem = async (item, checked) => {
    const updatedItems = (attributes[editingAttributeType] || []).map(i => 
      i.id === item.id ? { ...i, active: checked } : i
    );
    try {
      await axios.put(`${API}/settings/attributes/${editingAttributeType}`, 
        updatedItems, 
        { headers }
      );
      setAttributes({ ...attributes, [editingAttributeType]: updatedItems });
    } catch (error) {
      alert('Error updating item');
    }
  };

  const deleteItem = async (itemId) => {
    try {
      await axios.delete(`${API}/settings/attributes/${editingAttributeType}/${itemId}`, { headers });
      const res = await axios.get(`${API}/settings/attributes/all`, { headers });
      setAttributes(res.data);
    } catch (error) {
      alert('Error deleting item');
    }
  };

  const attributeTypes = [
    { key: 'categories', label: 'Categories' },
    { key: 'cities', label: 'Cities' },
    { key: 'genders', label: 'Genders' },
    { key: 'services', label: 'Services' },
    { key: 'eye_colors', label: 'Eye Colors' },
    { key: 'hair_colors', label: 'Hair Colors' },
    { key: 'body_types', label: 'Body Types' },
    { key: 'heights', label: 'Heights' }
  ];

  return (
    <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
      <h2 className="text-xl font-semibold text-white mb-6">Manage Attributes</h2>
      <p className="text-zinc-400 text-sm mb-6">
        Manage options for categories, cities, genders, services and physical attributes. These will be available when users create listings.
      </p>
      
      {/* Attribute Type Selector */}
      <div className="flex flex-wrap gap-2 mb-6">
        {attributeTypes.map(attr => (
          <Button
            key={attr.key}
            variant={editingAttributeType === attr.key ? 'default' : 'outline'}
            onClick={() => setEditingAttributeType(attr.key)}
            className={editingAttributeType === attr.key 
              ? 'bg-emerald-600 hover:bg-emerald-700' 
              : 'border-zinc-700 text-zinc-300 hover:bg-zinc-800'
            }
          >
            {attr.label}
            <Badge className="ml-2 bg-zinc-700 text-zinc-300">
              {(attributes[attr.key] || []).length}
            </Badge>
          </Button>
        ))}
      </div>

      {/* Add New Item */}
      <div className="flex gap-2 mb-6">
        <Input
          value={newAttributeItem.name}
          onChange={(e) => setNewAttributeItem({ name: e.target.value })}
          className="bg-zinc-800 border-zinc-700 text-white flex-1"
          placeholder={`Add new ${editingAttributeType.replace('_', ' ').replace(/s$/, '')}...`}
          onKeyPress={(e) => {
            if (e.key === 'Enter') addItem();
          }}
        />
        <Button onClick={addItem} className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="w-4 h-4 mr-2" />
          Add
        </Button>
      </div>

      {/* Items List */}
      <div className="space-y-2">
        {(attributes[editingAttributeType] || []).map((item) => (
          <div 
            key={item.id} 
            className={`flex items-center justify-between p-3 rounded-lg border ${
              item.active ? 'bg-zinc-800 border-zinc-700' : 'bg-zinc-800/50 border-zinc-700/50'
            }`}
          >
            <div className="flex items-center gap-3">
              <span className={`text-sm ${item.active ? 'text-white' : 'text-zinc-500'}`}>
                {item.name}
              </span>
              <Badge className={item.active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                {item.active ? 'Active' : 'Passive'}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={item.active}
                onCheckedChange={(checked) => toggleItem(item, checked)}
              />
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-white">Delete Item</AlertDialogTitle>
                    <AlertDialogDescription className="text-zinc-400">
                      Are you sure you want to delete "{item.name}"? This may affect existing listings.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-zinc-800 text-zinc-300 border-zinc-700">
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-red-600 hover:bg-red-700"
                      onClick={() => deleteItem(item.id)}
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
        {(attributes[editingAttributeType] || []).length === 0 && (
          <p className="text-zinc-500 text-center py-8">No items in this category yet.</p>
        )}
      </div>
    </div>
  );
};

export default AttributesTab;
